import React, { useEffect, useState } from 'react';
import { Input, Spin } from 'antd';
import moment from 'moment/moment.js';
import Segmented from '../../../components/segmented/Segmented.jsx';
import Pagination from '../../../components/pagination/Pagination.jsx';
import TableWithSkeleton from '../../../components/table/TableWithSkeleton.jsx';
import ModalComponent from '../../../components/modals/ModalComponent.jsx';
import Eye from '../../../assets/Eye.jsx';
import { GetAllAccountApi, GetAllV2fxAccountByIdApi } from '../../../api/request/liveAccount.js';
import pendingColumns from '../../../columns/ib/MT5Account/MT5PendingAccounts.js';
import acceptedColumns from '../../../columns/ib/MT5Account/MT5AcceptedAccounts.js';
import rejectColumns from '../../../columns/ib/MT5Account/MT5RejectAccount.js';

export default function LiveAccountMobile() {
  const [value, setValue] = useState('Pending');
  const [search, setSearch] = useState('');
  const [searchText, setSearchText] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecord, setTotalRecord] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState([]);
  const [sorter, setSorter] = useState({ SortBy: 'createdDate', SortOrder: '' });
  const [selectedV2fxId, setSelectedV2fxId] = useState('');
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [V2fxDetails, setV2fxDetails] = useState({});
  const [isUserLoading, setIsUserLoading] = useState(false);

  const columns =
    value === 'Approved' ? acceptedColumns : value === 'Declined' ? rejectColumns : pendingColumns;

  const getAccounts = async () => {
    try {
      setIsLoading(true);
      const params = {
        PageNumber: page,
        PageSize: pageSize,
        SearchText: searchText,
        FilterText: value,
        ...sorter,
      };
      const { data } = await GetAllAccountApi(new URLSearchParams(params).toString());
      if (data?.success) {
        setData(
          (data?.data || []).map((item) => {
            const fullName = `${item?.firstName || ''} ${item?.lastName || ''}`.trim();
            return {
              ...item,
              name: fullName,
              fullName: item?.fullName || fullName,
              createdDate: item?.createdDate ? moment(item.createdDate).format('DD-MM-YYYY, hh:mm A') : null,
              balance: `$ ${item?.balance ?? 0}`,
              actions:
                value === 'Approved' ? (
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedV2fxId(item?.terminalID);
                      setIsOpenModal(true);
                    }}
                  >
                    <Eye />
                  </button>
                ) : null,
            };
          })
        );
        setTotalRecord(data?.totalRecords || 0);
      } else {
        setData([]);
        setTotalRecord(0);
      }
    } catch (error) {
      setData([]);
      setTotalRecord(0);
    } finally {
      setIsLoading(false);
    }
  };

  const getV2fxById = async () => {
    try {
      setIsUserLoading(true);
      const { data } = await GetAllV2fxAccountByIdApi(selectedV2fxId);
      setV2fxDetails(data?.data || {});
    } catch (error) {
    } finally {
      setIsUserLoading(false);
    }
  };

  useEffect(() => {
    getAccounts();
  }, [page, pageSize, searchText, value, sorter]);

  useEffect(() => {
    if (selectedV2fxId) getV2fxById();
  }, [selectedV2fxId]);

  const handleTableChange = (pagination, filters, tableSorter) => {
    if (tableSorter?.order) {
      setSorter({ SortBy: tableSorter?.field, SortOrder: tableSorter?.order });
    } else {
      setSorter({ SortBy: 'createdDate', SortOrder: '' });
    }
  };

  return (
    <div className="space-y-4">
      <Segmented
        options={['Pending', 'Approved', 'Declined']}
        value={value}
        setValue={(next) => {
          setValue(next);
          setPage(1);
        }}
      />

      <Input
        size="large"
        placeholder="Search live account"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        onPressEnter={() => {
          setSearchText(search);
          setPage(1);
        }}
        className="rounded-xl"
      />

      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <TableWithSkeleton columns={columns} data={data} loading={isLoading} onChange={handleTableChange} />
        </div>
      </div>

      <Pagination total={totalRecord} page={page} setPage={setPage} pageSize={pageSize} setPageSize={setPageSize} />

      <ModalComponent isOpen={isOpenModal} setIsOpen={setIsOpenModal} title="Account Information" width="100%">
        {isUserLoading ? (
          <div className="flex justify-center flex-col my-10">
            <Spin size="large" />
            <p className="primary text-center mt-2">Loading...</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            {[
              ['Name', V2fxDetails?.fullName || `${V2fxDetails?.firstName || ''} ${V2fxDetails?.lastName || ''}`.trim()],
              ['Account No.', V2fxDetails?.terminalID || V2fxDetails?.login],
              ['Account Type', V2fxDetails?.tradingAccountType],
              ['Plan', V2fxDetails?.planName],
              ['Leverage', V2fxDetails?.leverage],
              ['Balance', V2fxDetails?.balance != null ? `$ ${V2fxDetails.balance}` : null],
              ['Currency', V2fxDetails?.currency],
              ['Created', V2fxDetails?.createdDate ? moment(V2fxDetails.createdDate).format('DD-MM-YYYY LT') : null],
            ].map(([label, val]) => (
              <div key={label} className="bg-gray-50 rounded-xl p-3">
                <p className="text-xs text-gray-500">{label}</p>
                <p className="text-sm font-semibold primary mt-1 break-all">{val || '--'}</p>
              </div>
            ))}
          </div>
        )}
      </ModalComponent>
    </div>
  );
}
